import Preloader from "@/common/components/Preloader/Preloader";
import { ScrollProvider } from '@/common/components/scrollContext';
import Template from '@/common/layout/template/Template';
import { initI18n } from "@/i18n/i18n-config";
import store from "@/rematch/store/store";
import type { AppProps } from "next/app";
import Router, { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Provider } from "react-redux";
import { httpwithtoken } from "../services/http";
import { DefaultSeo } from "next-seo";
import SEO from "../../next-seo.config";

function App({ Component, pageProps }: AppProps) {
  const [isClient, setIsClient] = useState(false);
  const router = useRouter();
  const [i18nLoaded, setI18nLoaded] = useState(false);
  const [i18nInstance, setI18nInstance] = useState<any>(null);

  useEffect(() => {
    const path = window.location.pathname;
    initI18n(path).then((i18nInst) => {
      if (i18nInst.isInitialized && Object.keys(i18nInst.store.data).length > 0) {
        setI18nLoaded(true);
        setI18nInstance(i18nInst);
      } else {
        i18nInst.on('loaded', () => {
          setI18nLoaded(true);
          setI18nInstance(i18nInst);
        });
      }
    }).catch((error) => {
      console.error('Failed to initialize i18n:', error);
    });

    return () => {
      if (i18nInstance) {
        i18nInstance.off('loaded');
      }
    };
  }, []);

  useEffect(() => {
    // Load non-critical styles
    const loadStyles = async () => {
      await Promise.all([

        import("../styles/bootstrap-icons-fix.css"),

        import("../styles/globals.scss")
      ]);
    };

    // Load Bootstrap JS
    const loadBootstrap = async () => {
      if (typeof window !== 'undefined') {
        await import('bootstrap/dist/js/bootstrap');
      }
    };

    // Load all non-critical resources
    Promise.all([loadStyles(), loadBootstrap()]).then(() => {
      setIsClient(true);
    });
  }, []);

  // Google Analytics
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (window.gtag && process.env.NEXT_PUBLIC_GA_ID) {
        window.gtag("config", process.env.NEXT_PUBLIC_GA_ID, {
          page_path: url,
        });
      }
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  // HTTP interceptor
  useEffect(() => {
    const interceptor = httpwithtoken.interceptors.response.use(
      (response) => response,
      (error) => {
        if (router.pathname === "/admin" && error?.response?.status === 401) {
          Router.push("/login");
        }
        return Promise.reject(error);
      }
    );

    return () => {
      httpwithtoken.interceptors.response.eject(interceptor);
    };
  }, [router.pathname]);

  if (!isClient || !i18nLoaded) {
    return <Preloader />;
  }

  return (
    <Provider store={store}>
    <DefaultSeo {...SEO} />
      <DndProvider backend={HTML5Backend}>
        <Template>
          <ScrollProvider>
            <Component {...pageProps} />
          </ScrollProvider>
        </Template>
      </DndProvider>
    </Provider>
  );
}

// Add type declaration for gtag
declare global {
  interface Window {
    gtag: (command: string, id: string, config: any) => void;
  }
}

export default App;
